import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { contact } from "../services/LoginService"; // Vérifie le chemin
import { Helmet } from "react-helmet-async";
import { Loader2, Mail, MessageSquare, Send, User } from "lucide-react";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MESSAGE_MAX = 2000;

function champErreur(champ, valeur) {
    switch (champ) {
        case "nom":
            return valeur.trim().length < 2 ? "Merci d'indiquer votre nom." : "";
        case "email":
            return !EMAIL_REGEX.test(valeur.trim()) ? "Adresse email invalide." : "";
        case "message":
            if (valeur.trim().length < 10) return "Votre message est un peu court (10 caractères min).";
            if (valeur.length > MESSAGE_MAX) return `Maximum ${MESSAGE_MAX} caractères.`;
            return "";
        default:
            return "";
    }
}

const Champ = ({ label, icone: Icone, error, children }) => (
    <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-2 text-color2 font-medium text-sm">
            <Icone size={15} className="opacity-70" aria-hidden="true" />
            {label}
        </label>
        {children}
        {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
);

const inputClasses = (hasError) =>
    `w-full px-4 py-3 rounded-lg border text-black transition
     focus:outline-none focus:ring-2 focus:ring-color1
     ${hasError ? "border-red-400" : "border-gray-300"}`;

export default function ContactPage() {
    const [nom, setNom] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [erreurs, setErreurs] = useState({});

    const validerChamp = (champ, valeur) => {
        setErreurs((prev) => ({ ...prev, [champ]: champErreur(champ, valeur) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const nouvellesErreurs = {
            nom: champErreur("nom", nom),
            email: champErreur("email", email),
            message: champErreur("message", message),
        };
        setErreurs(nouvellesErreurs);

        if (Object.values(nouvellesErreurs).some(Boolean)) {
            toast.error("Merci de corriger les champs en rouge.", { position: "top-right", autoClose: 4000 });
            return;
        }

        setIsLoading(true);

        const formData = { nom, email, message };

        try {
            const result = await contact(formData);
            if (result.success) {
                toast.success(result.message, { position: "top-right", autoClose: 5000 });
                setNom("");
                setEmail("");
                setMessage("");
                setErreurs({});
            } else {
                toast.error(result.message, { position: "top-right", autoClose: 5000 });
            }
        } catch (err) {
            console.error(err);
            toast.error("Erreur inattendue lors de l'envoi.", { position: "top-right", autoClose: 5000 });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Nous contacter — Thrundrz</title>
                <meta name="description" content="Une question sur Thrundrz ? Contactez notre équipe via le formulaire. Nous vous répondons rapidement." />
                <link rel="canonical" href="https://thrundrz.fr/contact" />
            </Helmet>
            <Navbar />
            <main className="min-h-screen bg-black py-16 px-4 lg:pt-28">
                <div className="max-w-3xl mx-auto bg-color4 shadow-lg rounded-2xl p-8">
                    <h1 className="text-3xl font-bold text-color2 mb-3 text-center font-archivo">
                        Nous contacter
                    </h1>
                    <p className="text-color2/80 text-center mb-8 max-w-xl mx-auto">
                        Une question, une suggestion, un souci technique ? Écrivez-nous,
                        notre équipe vous répond généralement sous 48h.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                        {/* Nom */}
                        <Champ label="Nom complet" icone={User} error={erreurs.nom}>
                            <input
                                type="text"
                                autoComplete="name"
                                value={nom}
                                onChange={(e) => setNom(e.target.value)}
                                onBlur={(e) => validerChamp("nom", e.target.value)}
                                placeholder="Votre nom"
                                className={inputClasses(erreurs.nom)}
                            />
                        </Champ>

                        {/* Email */}
                        <Champ label="Adresse e-mail" icone={Mail} error={erreurs.email}>
                            <input
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={(e) => validerChamp("email", e.target.value)}
                                placeholder="exemple@email.com"
                                className={inputClasses(erreurs.email)}
                            />
                        </Champ>

                        {/* Message */}
                        <Champ label="Message" icone={MessageSquare} error={erreurs.message}>
                            <textarea
                                rows="5"
                                maxLength={MESSAGE_MAX}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onBlur={(e) => validerChamp("message", e.target.value)}
                                placeholder="Écrivez votre message ici..."
                                className={inputClasses(erreurs.message) + " resize-none"}
                            />
                            <span className="text-[11px] text-color2/50 self-end">
                                {message.length}/{MESSAGE_MAX}
                            </span>
                        </Champ>

                        {/* Bouton */}
                        <div className="text-center pt-1">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`inline-flex items-center justify-center gap-2 bg-color1 text-white
                                            font-semibold px-8 py-3 rounded-lg hover:bg-color9
                                            transition duration-200 ${isLoading ? "cursor-not-allowed" : ""}`}
                            >
                                {isLoading ? (
                                    <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                                ) : (
                                    <Send size={16} aria-hidden="true" />
                                )}
                                {isLoading ? "Envoi en cours..." : "Envoyer le message"}
                            </button>
                        </div>
                    </form>
                    <ToastContainer />
                </div>
            </main>
            <Footer />
        </>
    );
}